import React, { useState, useEffect } from 'react';
import { Eye, X, AlertTriangle, Shield, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';

export const ImpersonationBanner = () => {
  const [session, setSession] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    // Check for impersonation session
    const impSession = localStorage.getItem('impersonation_session');
    if (impSession) {
      try {
        const parsed = JSON.parse(impSession);
        setSession(parsed);
        
        // Calculate time remaining
        if (parsed.expires_at) {
          const expiresAt = new Date(parsed.expires_at);
          const updateTimer = () => {
            const now = new Date();
            const diff = expiresAt - now;
            if (diff <= 0) {
              handleEndImpersonation();
              return;
            }
            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
          };
          updateTimer();
          const interval = setInterval(updateTimer, 1000);
          return () => clearInterval(interval);
        }
      } catch (e) {
        console.error('Failed to parse impersonation session:', e);
      }
    }
  }, []);

  const handleEndImpersonation = async () => {
    try {
      // Call admin API to end impersonation
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        localStorage.setItem('token', adminToken);
        await api.post('/admin/impersonate/end').catch(() => {});
      }
    } catch (error) {
      console.error('Failed to end impersonation:', error);
    }
    
    // Clear impersonation session
    localStorage.removeItem('impersonation_session');
    localStorage.removeItem('impersonation_token');
    localStorage.removeItem('user');
    
    // Restore admin token and redirect to admin console
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
    }
    
    toast.success('Impersonation session ended');
    window.location.href = '/admin?section=tenants';
  };

  if (!session) return null;

  const isWriteMode = session.access_level === 'write';

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[100] h-12 px-4 flex items-center justify-between shadow-lg ${
        isWriteMode 
          ? 'bg-gradient-to-r from-red-600 to-red-700 text-white' 
          : 'bg-gradient-to-r from-slate-800 to-slate-900 text-white border-b border-amber-500/50'
      }`}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-4">
        <div className={`h-7 w-7 rounded-md flex items-center justify-center ${
          isWriteMode ? 'bg-red-700' : 'bg-amber-500'
        }`}>
          <Shield className="h-4 w-4 text-white" />
        </div>
        
        <div className="flex items-center gap-2 text-sm">
          <span className="font-bold tracking-wide text-amber-400">
            ADMIN VIEW
          </span>
          <span className="text-slate-400">—</span>
          <span className="text-slate-200">
            Viewing as: <span className="font-semibold text-white">{session.tenant_name || 'Unknown Tenant'}</span>
          </span>
        </div>
        
        <Badge 
          className={`text-xs ${
            isWriteMode 
              ? 'bg-red-800 text-white border-red-900' 
              : 'bg-amber-500/20 text-amber-400 border-amber-500/40'
          }`}
        >
          {isWriteMode ? '⚠️ WRITE MODE' : '👁️ Read-Only'}
        </Badge>
        
        {timeRemaining && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded-md">
            <Clock className="h-3.5 w-3.5" />
            <span>{timeRemaining} remaining</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-3">
        {isWriteMode && (
          <div className="hidden md:flex items-center gap-2 text-xs text-red-200">
            <AlertTriangle className="h-4 w-4" />
            <span>All actions are logged</span>
          </div>
        )}
        
        <Button 
          size="sm" 
          onClick={handleEndImpersonation}
          className={`gap-1.5 h-8 text-xs font-medium ${
            isWriteMode 
              ? 'bg-red-800 hover:bg-red-900 text-white' 
              : 'bg-slate-700 hover:bg-slate-600 text-white'
          }`}
          data-testid="exit-impersonation-btn"
        >
          <X className="h-3.5 w-3.5" />
          Exit
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
